import { LanguagesEnum } from '@/api/enums';
import { PhishingLanguagesEnum } from '@/api/enums/phishing-languages.enum';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { PhishingLanguageTargetingMethod } from '@/client/accounts/types/account.type';
import {
  CampaignTemplate,
  SimulationPlanData,
  SimulationSchedulerItem,
} from '@/client/campaigns';
import { CatalogueVariant } from '@/client/campaigns/types/catalogue.type';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { ExcludeFormValue, TargetFormValue } from '@/common/types/targets.type';
import { CatalogueSelectorModal } from '@/components/campaigns';
import { AddButton } from '@/components/campaigns/simulations/AddButton';
import { SendTestEmailForm } from '@/components/campaigns/simulations/SendTestEmailForm';
import { SimulationTemplateCard } from '@/components/campaigns/simulations/SimulationTemplateCard';
import { PlanSimulationLanguageField } from '@/components/campaigns/simulations/modals/PlanSimulationLanguageField';
import { useAccount } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGenerateCustomizedSimulation } from '@/hooks/useGenerateCustomizedSimulation';

import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { FlexContainer } from '@/ui';
import { AppButton } from '@/ui/buttons';
import { AppCalendarWithCustomTimepicker } from '@/ui/calendar/AppCalendarWithCustomTimepicker';
import { TargetEntitiesSelector } from '@/ui/target-selector';
import { hasRestrictedUserScope } from '@/utils/custom-roles';
import {
  isDemoRestrictedMode,
  mapLanguageToPhishingLanguage,
} from '@/utils/helpers';
import { EditIcon, WarningTriangleIcon } from '@/utils/helpers/icons.helper';
import { PostHogFeatureFlags } from '@/utils/posthog';
import {
  getDefaultTargetGroup,
  getTargetsAndExcludes,
  processIncludeExcludeTargetEntities,
} from '@/utils/targets';
import { getAppAbsoluteUrl } from '@/utils/url';
import moment from 'moment';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 649px;

  label {
    font-weight: 400;
  }
`;

const SimulationSelectorContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EditIconButton = styled.div`
  svg path {
    fill: var(--green-color);
  }

  cursor: pointer;
  padding: var(--xxsmall-padding);
  margin-top: var(--xxsmall-padding);
`;

const SimulationTemplateDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  border: 1px solid var(--gray-light);
  border-radius: var(--box-border-radius);
  background: white;
  min-height: 80px;
  padding-right: var(--medium-padding);
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--medium-padding);
`;

const StyledAccordion = styled(Accordion)`
  .p-accordion-tab {
    border: 1px solid var(--gray-moderate);
    border-radius: var(--xsmall-border-radius);
    overflow: hidden;
  }

  .p-accordion-header-link {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    padding: var(--medium-padding) var(--default-padding-small);
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: var(--small-font-size);
    background: transparent;
    border: none;
  }

  .p-accordion-toggle-icon {
    margin-right: 0;
  }

  .p-accordion-content {
    padding: 0 var(--default-padding-small) var(--default-padding-small);
    border: none;
  }
`;

const StyledUnverifiedDomainWarning = styled.div`
  width: 100%;
  background-color: var(--yellow-pale);
  border-radius: var(--small-border-radius);
  padding: var(--default-padding-small) var(--default-padding);
  margin-top: var(--medium-padding);
  padding-left: var(--default-padding-small);
  display: flex;
  gap: var(--xsmall-padding);
  align-items: flex-start;

  svg {
    flex-shrink: 0;
  }

  .warning-text {
    display: flex;
    flex-direction: column;
    gap: var(--small-padding);

    p {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      margin: 0;
    }

    .warning-link {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      color: var(--black-main);
      cursor: pointer;
      text-decoration: none;
      border-bottom: 2px solid var(--gray-buttom-border);
      background: none;
      padding-bottom: 6px;
    }
  }
`;

const getSelectedVariantLanguage = (
  templateId: string,
  variants: CatalogueVariant[] | undefined,
  language: LanguagesEnum | null | undefined,
): PhishingLanguagesEnum | undefined =>
  variants?.find((variant) => variant.templateId === templateId)?.language ??
  (language ? mapLanguageToPhishingLanguage(language) ?? undefined : undefined);

type PlanSimulationModalProps = {
  selectedTask: SimulationSchedulerItem | null;
  isOpen: boolean;
  onClose: () => void;
  onPlanSimulation: (data: SimulationPlanData) => void;
  selectedCampaignTemplate?: CampaignTemplate & {
    variants?: CatalogueVariant[];
  };
  isLoadingCampaignTemplate?: boolean;
  isRecommendedSimulation?: boolean;
};

export const PlanSimulationModal: React.FC<PlanSimulationModalProps> = ({
  isOpen,
  selectedTask,
  onClose,
  onPlanSimulation,
  selectedCampaignTemplate,
  isRecommendedSimulation = false,
}) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(currentAccount, currentUser);
  const isScopeRestricted = hasRestrictedUserScope(
    currentUser?.customRole?.scopeMode,
  );
  const { accountCanUseFeature } = useFeatureFlag();
  const isTemplateLevelView = accountCanUseFeature(
    PostHogFeatureFlags.PHISHING_TEMPLATE_LEVEL_VIEW,
    currentAccount?.featureFlagAbility ?? [],
  );
  const { account: freshAccount } = useAccount({
    accountId: currentAccount?.id,
    enabled: isOpen && !!currentAccount?.id && !currentAccount?.isSystem,
  });
  const languageTargetingMethod =
    freshAccount?.settings?.phishingLanguageTargeting?.method;
  const isLanguageTargetingActive =
    isTemplateLevelView &&
    accountCanUseFeature(
      PostHogFeatureFlags.SMART_LANGUAGE_TARGETING,
      freshAccount?.featureFlagAbility ?? [],
    ) &&
    (languageTargetingMethod === PhishingLanguageTargetingMethod.SINGLE ||
      languageTargetingMethod === PhishingLanguageTargetingMethod.AUTO);
  const [showCampaignTemplatesModal, setShowCampaignTemplatesModal] =
    useState<boolean>(false);

  const {
    isPreparing,
    isFailed,
    showChip,
    isSubModalOpen,
    handleLaunch,
    modals: generateSimulationModals,
  } = useGenerateCustomizedSimulation({
    isActive: isOpen,
    onUseEmailTemplate: (campaignTemplate?: CampaignTemplate) => {
      setFormData((prev) => ({
        ...prev,
        campaignTemplateId: campaignTemplate?.id,
        difficulty: campaignTemplate?.difficulty ?? undefined,
        phishingSign: campaignTemplate?.meta?.phishingSign ?? undefined,
        name: campaignTemplate?.name,
        isGeneratedWithAI: true,
        language: undefined,
        variants: undefined,
      }));
    },
  });

  const [excludedDomainUnverifiedCount, setExcludedDomainUnverifiedCount] =
    useState<number>(0);
  const [formData, setFormData] = useState<SimulationPlanData>({
    id: selectedTask?.id,
    campaignTemplateId: undefined,
    difficulty: undefined,
    phishingSign: undefined,
    name: undefined,
    startDate: undefined,
    endDate: undefined,
    targetGroup: getDefaultTargetGroup(
      currentAccount as Account,
      isScopeRestricted,
    ),
    excludes: [],
    isGeneratedWithAI: selectedTask?.isGeneratedWithAI,
  });
  const [excludesChanged, setExcludesChanged] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen || !selectedCampaignTemplate) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      campaignTemplateId: selectedCampaignTemplate.id,
      difficulty: selectedCampaignTemplate.difficulty,
      phishingSign: selectedCampaignTemplate.meta?.phishingSign,
      name: selectedCampaignTemplate.name,
      variants: selectedCampaignTemplate.variants,
      language: getSelectedVariantLanguage(
        selectedCampaignTemplate.id,
        selectedCampaignTemplate.variants,
        selectedCampaignTemplate.language,
      ),
    }));
  }, [selectedCampaignTemplate, isOpen]);

  const targetGroupKey = (formData.targetGroup ?? [])
    .map((t) => t.id)
    .join(',');
  const excludesKey = (formData.excludes ?? []).map((e) => e.id).join(',');

  useEffect(() => {
    if (!isOpen || !currentAccount?.id) return;

    client.planner
      .targetSelectorPreview(
        {
          ...getTargetsAndExcludes(
            formData.targetGroup ?? [],
            formData.excludes ?? [],
          ),
          domainVerifiedOnly: true,
        },
        currentAccount.id,
      )
      .then((res) =>
        setExcludedDomainUnverifiedCount(res.excludedDomainUnverifiedCount),
      )
      .catch(() => setExcludedDomainUnverifiedCount(0));
  }, [targetGroupKey, excludesKey, isOpen, currentAccount?.id]);

  const isToday = (date?: Date | null): boolean => {
    if (!date) return false;

    const today = moment().startOf('day');
    const dateToCheck = moment(date).startOf('day');
    return today.isSame(dateToCheck, 'day');
  };

  const getDefaultStartTime = (date: Date | null): Date | undefined => {
    if (!date) return undefined;
    const dateMoment = moment(date);

    if (isToday(date)) {
      const baseHour = 9;
      const now = moment();
      const defaultTime = dateMoment
        .hour(baseHour)
        .minute(0)
        .second(0)
        .millisecond(0);

      if (defaultTime.isBefore(now)) {
        return now.clone().add(60, 'minutes').toDate();
      }

      return defaultTime.toDate();
    }

    return dateMoment.hour(9).minute(0).second(0).millisecond(0).toDate();
  };

  const getDefaultEndTime = (
    startDate: Date | null,
    endDate: Date | null,
  ): Date | undefined => {
    if (!startDate || !endDate) return undefined;

    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    if (isToday(startDate) && isToday(endDate)) {
      const defaultTime = endMoment.hour(15).minute(0).second(0).millisecond(0);

      const minEndTime = startMoment
        .clone()
        .hour(15)
        .minute(0)
        .second(0)
        .millisecond(0);
      if (defaultTime.isBefore(minEndTime)) {
        return minEndTime.toDate();
      }

      return defaultTime.toDate();
    }

    const minEndTime = startMoment.clone().add(1, 'hour');
    if (endMoment.isBefore(minEndTime)) {
      return minEndTime.toDate();
    }

    if (startMoment.isSame(endMoment, 'day')) {
      return endMoment.hour(15).minute(0).second(0).millisecond(0).toDate();
    }

    return endDate;
  };

  const getEndDateMinDate = (): Date => {
    if (!formData.startDate) {
      return moment().toDate();
    }

    return moment(formData.startDate).add(1, 'hour').toDate();
  };

  const handleStartDateChange = (value: Date | null) => {
    const newStartDate = value ?? undefined;
    let newEndDate = formData.endDate;

    if (newStartDate) {
      const prevDate = formData.startDate;
      const dateChanged =
        !prevDate || !moment(newStartDate).isSame(moment(prevDate), 'day');

      if (dateChanged) {
        if (isToday(newStartDate)) {
          const defaultStartTime = getDefaultStartTime(newStartDate);
          if (defaultStartTime) {
            newStartDate.setHours(defaultStartTime.getHours());
            newStartDate.setMinutes(defaultStartTime.getMinutes());
            newStartDate.setSeconds(0);
            newStartDate.setMilliseconds(0);
          }
        } else {
          newStartDate.setHours(9);
          newStartDate.setMinutes(0);
          newStartDate.setSeconds(0);
          newStartDate.setMilliseconds(0);
        }
      }
    }

    if (newStartDate && newEndDate) {
      const minEndTime = moment(newStartDate)
        .hour(15)
        .minute(0)
        .second(0)
        .millisecond(0);
      if (moment(newEndDate).isBefore(minEndTime)) {
        if (isToday(newStartDate) && isToday(newEndDate)) {
          const adjustedEndDate = getDefaultEndTime(newStartDate, newEndDate);
          if (adjustedEndDate) {
            newEndDate = adjustedEndDate;
          }
        } else {
          newEndDate = minEndTime.toDate();
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      startDate: newStartDate,
      endDate: newEndDate,
    }));
  };

  const handleEndDateChange = (value: Date | null) => {
    const newEndDate = value ?? undefined;
    const startDate = formData.startDate;

    if (newEndDate) {
      const prevEndDate = formData.endDate;
      const dateChanged =
        !prevEndDate || !moment(newEndDate).isSame(moment(prevEndDate), 'day');

      if (dateChanged) {
        if (startDate && isToday(startDate) && isToday(newEndDate)) {
          const defaultEndTime = getDefaultEndTime(startDate, newEndDate);
          if (defaultEndTime) {
            newEndDate.setHours(defaultEndTime.getHours());
            newEndDate.setMinutes(defaultEndTime.getMinutes());
            newEndDate.setSeconds(0);
            newEndDate.setMilliseconds(0);
          }
        } else if (startDate && newEndDate) {
          if (!isToday(newEndDate)) {
            newEndDate.setHours(15);
            newEndDate.setMinutes(0);
            newEndDate.setSeconds(0);
            newEndDate.setMilliseconds(0);
          }
        } else if (!startDate) {
          if (!isToday(newEndDate)) {
            newEndDate.setHours(15);
            newEndDate.setMinutes(0);
            newEndDate.setSeconds(0);
            newEndDate.setMilliseconds(0);
          }
        }
      }

      if (startDate && newEndDate) {
        const minEndTime = moment(startDate).add(1, 'hour');
        if (moment(newEndDate).isBefore(minEndTime)) {
          const adjustedEndTime = minEndTime.toDate();
          newEndDate.setHours(adjustedEndTime.getHours());
          newEndDate.setMinutes(adjustedEndTime.getMinutes());
          newEndDate.setSeconds(0);
          newEndDate.setMilliseconds(0);
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      endDate: newEndDate,
    }));
  };

  const handleSubmit = () => {
    onPlanSimulation({
      ...formData,
      excludes: excludesChanged ? formData.excludes : undefined,
    });
    setFormData({
      campaignTemplateId: undefined,
      startDate: undefined,
      endDate: undefined,
      targetGroup: getDefaultTargetGroup(
        currentAccount as Account,
        isScopeRestricted,
      ),
      excludes: [],
      difficulty: undefined,
      phishingSign: undefined,
      name: undefined,
    });
    setExcludesChanged(false);
    onClose();
  };

  const handleClose = () => {
    setFormData({
      id: undefined,
      campaignTemplateId: undefined,
      difficulty: undefined,
      phishingSign: undefined,
      name: undefined,
      startDate: undefined,
      endDate: undefined,
      targetGroup: getDefaultTargetGroup(
        currentAccount as Account,
        isScopeRestricted,
      ),
      excludes: [],
      language: undefined,
      variants: undefined,
    });
    setExcludesChanged(false);
    onClose();
  };

  useEffect(() => {
    setExcludesChanged(false);
  }, [selectedTask, isOpen]);

  useEffect(() => {
    if (selectedTask) {
      const { includedEntities, excludedEntities } =
        processIncludeExcludeTargetEntities({
          target: selectedTask.targetGroup ?? {},
          excludes: selectedTask.excludes ?? {},
          account: currentAccount as Account,
        });

      setFormData({
        id: selectedTask.id,
        campaignTemplateId: selectedTask.campaignTemplateId,
        difficulty: selectedTask.difficulty,
        phishingSign: selectedTask.phishingSign,
        name: selectedTask.name,
        startDate: selectedTask.startDate ?? undefined,
        endDate: selectedTask.endDate ?? undefined,
        targetGroup: includedEntities,
        excludes: excludedEntities,
        isGeneratedWithAI: selectedTask.isGeneratedWithAI,
        language: selectedTask.language,
      });
    } else if (!selectedCampaignTemplate) {
      setFormData({
        campaignTemplateId: undefined,
        difficulty: undefined,
        phishingSign: undefined,
        name: undefined,
        startDate: undefined,
        endDate: undefined,
        targetGroup: getDefaultTargetGroup(
          currentAccount as Account,
          isScopeRestricted,
        ),
        excludes: [],
        id: undefined,
      });
    }
  }, [selectedTask, isOpen, selectedCampaignTemplate]);

  return (
    <>
      <StyledDialog
        visible={isOpen && !isSubModalOpen}
        onHide={handleClose}
        header={
          selectedTask
            ? t('phishing.simulations.modal.edit.title')
            : t('phishing.simulations.modal.plan.title')
        }
        modal
        draggable={false}
        resizable={false}
        className="v2"
      >
        <div className="field w-full mb-4">
          <label>{t('phishing.simulations.modal.chooseSimulation')}</label>
          <SimulationSelectorContainer>
            {formData.campaignTemplateId ? (
              <SimulationTemplateDisplay>
                <SimulationTemplateCard
                  difficulty={formData.difficulty}
                  phishingSign={formData.phishingSign}
                  name={formData.name}
                  isGeneratedWithAI={formData.isGeneratedWithAI}
                />
                {!formData.id &&
                  !isRecommendedSimulation &&
                  !formData.isGeneratedWithAI && (
                    <EditIconButton
                      onClick={() => {
                        setShowCampaignTemplatesModal(true);
                      }}
                    >
                      <EditIcon />
                    </EditIconButton>
                  )}
              </SimulationTemplateDisplay>
            ) : (
              <FlexContainer gap={18}>
                <AddButton
                  title={t('phishing.simulations.modal.browseCatalogue')}
                  description={t(
                    'phishing.simulations.modal.browseCatalogue.descr',
                  )}
                  id="browse-catalogue"
                  onClick={() => {
                    setShowCampaignTemplatesModal(true);
                  }}
                  isModal
                />
                <AddButton
                  title={
                    isPreparing
                      ? t('phishing.simulations.modal.reconai.preparing')
                      : isFailed
                      ? t('phishing.simulations.modal.reconai.failed')
                      : t('phishing.simulations.modal.generateSimulation')
                  }
                  description={
                    isPreparing || isFailed
                      ? ''
                      : t('phishing.simulations.modal.generateSimulation.descr')
                  }
                  id="generate-simulation"
                  disabled={isPreparing}
                  onClick={handleLaunch}
                  chipLabel={
                    showChip
                      ? t('phishing.simulations.modal.reconai.chip')
                      : undefined
                  }
                  isModal
                />
              </FlexContainer>
            )}
          </SimulationSelectorContainer>
        </div>

        <div>
          <DateRow>
            <div className="field w-full mb-4">
              <label>
                {t('generic.startDate')} <span className="red"> *</span>
              </label>
              <AppCalendarWithCustomTimepicker
                value={formData.startDate}
                onChange={handleStartDateChange}
                minDate={moment().toDate()}
                placeholder={t('generic.select.startDate')}
                className="w-full"
                showTime
                withHolidays
              />
            </div>
            <div className="field w-full mb-4">
              <label>
                {t('generic.endDate')} <span className="red"> *</span>
              </label>
              <AppCalendarWithCustomTimepicker
                value={formData.endDate}
                onChange={handleEndDateChange}
                minDate={getEndDateMinDate()}
                maxDate={moment(formData.startDate ?? new Date())
                  .add(11, 'months')
                  .toDate()}
                placeholder={t('generic.select.endDate')}
                className="w-full"
                viewDate={formData.endDate ?? formData.startDate}
                showTime
                withHolidays
              />
            </div>
          </DateRow>
        </div>

        <div className="field w-full mb-4">
          <TargetEntitiesSelector
            includedEntities={
              formData.targetGroup ??
              getDefaultTargetGroup(
                currentAccount as Account,
                isScopeRestricted,
              )
            }
            className="phishing"
            title={t('phishing.simulations.modal.targetGroup')}
            required
            setIncludedEntities={(values: TargetFormValue[]) =>
              setFormData((prev) => ({
                ...prev,
                targetGroup: values,
              }))
            }
            excludedEntities={formData.excludes ?? []}
            setExcludedEntities={(values: ExcludeFormValue[]) => {
              setFormData((prev) => ({
                ...prev,
                excludes: values,
              }));
              setExcludesChanged(true);
            }}
          />
          {excludedDomainUnverifiedCount > 0 && (
            <StyledUnverifiedDomainWarning>
              <WarningTriangleIcon />
              <div className="warning-text">
                <p>
                  <strong>
                    {t('campaign.target.domainUnverified.count', {
                      count: excludedDomainUnverifiedCount,
                    })}
                  </strong>{' '}
                  {t('campaign.target.domainUnverified.description')}
                </p>
                <div>
                  <a
                    target="_blank"
                    className="warning-link"
                    onClick={() =>
                      window.open(
                        getAppAbsoluteUrl(
                          RedirectPaths[
                            RedirectPathsEnum
                              .ACCOUNT_EMAILS_AND_DELIVERY_DOMAINS
                          ](),
                        ),
                        '_blank',
                      )
                    }
                  >
                    {t('campaign.target.domainUnverified.link')}
                  </a>
                </div>
              </div>
            </StyledUnverifiedDomainWarning>
          )}
        </div>

        {!formData.id &&
          !isLanguageTargetingActive &&
          !!formData.variants?.length && (
            <PlanSimulationLanguageField
              variants={formData.variants}
              value={formData.campaignTemplateId}
              onChange={(variant) =>
                setFormData((prev) => ({
                  ...prev,
                  campaignTemplateId: variant.templateId,
                  language: variant.language,
                }))
              }
            />
          )}

        {!!formData.id &&
          !!selectedTask?.languageGroupId &&
          !!selectedTask.languages?.length && (
            <PlanSimulationLanguageField
              readOnly
              languages={selectedTask.languages}
            />
          )}

        {!!formData.id &&
          !selectedTask?.languageGroupId &&
          isTemplateLevelView &&
          selectedTask?.language && (
            <PlanSimulationLanguageField
              readOnly
              language={selectedTask.language}
            />
          )}

        {selectedTask?.id &&
          selectedTask?.emailTemplateId &&
          selectedTask?.sendingProfileId &&
          selectedTask?.landingPageId && (
            <div className="mb-4">
              <StyledAccordion>
                <AccordionTab header={t('simulations.testEmail.title')}>
                  <SendTestEmailForm
                    campaignId={selectedTask.testSendId ?? selectedTask.id}
                    emailTemplateId={selectedTask.emailTemplateId}
                    sendingProfileId={selectedTask.sendingProfileId}
                    landingPageId={selectedTask.landingPageId}
                    testSends={selectedTask.testSends}
                  />
                </AccordionTab>
              </StyledAccordion>
            </div>
          )}

        <FlexContainer justify="flex-start" gap={12}>
          <AppButton
            label={
              selectedTask
                ? t('phishing.simulations.modal.saveChanges')
                : t('phishing.simulations.modal.planSimulation')
            }
            isDisabled={
              !formData.campaignTemplateId ||
              !formData.startDate ||
              !formData.endDate
            }
            onClick={isDemoAccount ? undefined : handleSubmit}
          />
          <AppButton
            severity="secondary"
            type="outlined"
            label={t('button.cancel')}
            onClick={handleClose}
          />
        </FlexContainer>
      </StyledDialog>
      <CatalogueSelectorModal
        visible={showCampaignTemplatesModal}
        onHide={() => setShowCampaignTemplatesModal(false)}
        onSelect={(template) => {
          setFormData((prev) => ({
            ...prev,
            campaignTemplateId: template.id,
            difficulty: template.difficulty ?? undefined,
            phishingSign: template.phishingSign ?? undefined,
            name: template.name,
            variants: template.variants,
            language: getSelectedVariantLanguage(
              template.id,
              template.variants,
              template.language,
            ),
          }));
          setShowCampaignTemplatesModal(false);
        }}
        entryPoint="phishing-simulation"
      />
      {generateSimulationModals}
    </>
  );
};
